// apps/vendor auth provider.
//
// Same Phase-0 back-compat pattern as apps/main; migrated to the new
// createAuthClient() surface in a follow-up. Audience is injected via the
// VITE_AUTH_CLIENT_AUDIENCE env var (clearpath-vendor for this app).
//
// ENG-340 — this app previously granted access on any signed-in session, with
// no mandatory-MFA / enrollment-enforcement gate. Vendors (and any
// tenant_staff_manager reaching this audience) are a MANDATORY MFA class
// (services/auth-service/src/mfa/enforcement.ts). This provider now runs a
// mount-time enrollment gate — mirroring the "admin pages NEVER render without
// MFA" invariant in apps/admin/src/auth-provider.tsx, but as an enrollment
// check for the vendor audience: after sign-in, a mandatory-class principal
// with NO verified factor is routed into enrollment before any vendor surface
// renders. The sign-in cutover flag path (MFA-M1) is intentionally untouched.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getAccessToken,
  getCurrentUser,
  loadAuthConfig,
  signIn as authSignIn,
  signOut as authSignOut,
  type Session,
  type SignOutOptions,
  type SignOutResult,
} from "@clearpath/auth-client";
import {
  resolveActorClassFromAudience,
  resolveVendorMfaGate,
} from "./vendor-mfa-gate";
import { hydrateInitialSession } from "./session-hydration";

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  /**
   * True once a signed-in principal of a mandatory-MFA class is confirmed to
   * hold no verified factor. While true, `session` is withheld (null) so no
   * protected surface renders — the app shows the enrollment gate instead.
   */
  mfaEnrollmentRequired: boolean;
  signIn: (email?: string) => Promise<void>;
  /** Route the user into MFA enrollment via the hosted auth flow. */
  startMfaEnrollment: () => Promise<void>;
  signOut: (options?: SignOutOptions) => Promise<SignOutResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Live factor-state read. Mirrors the wire contract of the
 * packages/auth-client account-security helpers (`GET /account/mfa` →
 * `{ factors: [...] }`, pinned to this app's audience via
 * `X-Molaris-Audience`, refresh cookie via credentials:include). The vendor
 * audience is accepted by the server handler
 * (services/auth-service/src/identity/account_mfa.ts:audienceToPoolEnvKey →
 * TENANT_DEV_STAFF_POOL_ID). Uses the free-function `getAccessToken()` so it
 * rides the same default AuthClient the Apollo link already uses — no second
 * in-memory token store.
 *
 * Throws on missing token / non-2xx so the gate can FAIL CLOSED (a
 * mandatory-class principal whose factor state we cannot read is gated into
 * enrollment, never let in).
 */
async function vendorHasVerifiedFactor(): Promise<boolean> {
  const config = loadAuthConfig();
  const token = await getAccessToken();
  if (!token) {
    throw new Error("[vendor][auth] no access token for MFA factor lookup");
  }
  const url = new URL("/account/mfa", config.authBaseUrl).toString();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
      authorization: `Bearer ${token}`,
      "x-molaris-audience": config.audience,
    },
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`[vendor][auth] /account/mfa returned ${res.status}`);
  }
  const body = (await res.json()) as { factors?: unknown };
  return Array.isArray(body.factors) && body.factors.length > 0;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ENG-128 — tolerate the post-sign-in hydration race. A cold sign-in
      // reloads into "/" with a just-minted refresh cookie that can 401 the
      // first /me probe spuriously; getCurrentUser() surfaces that as a plain
      // null. Retrying once before concluding "logged out" keeps a valid
      // session from false-negativing to the sign-in screen (~1/3 of cold
      // sign-ins otherwise dropped here).
      const current = await hydrateInitialSession({ loadSession: getCurrentUser });
      if (cancelled) return;
      if (!current) {
        setSession(null);
        setMfaEnrollmentRequired(false);
        setLoading(false);
        return;
      }

      // MFA enforcement is bypassed on localhost dev so operators can iterate
      // without a live hosted sign-in flow — same carve-out as
      // apps/admin/src/auth-provider.tsx. Production/staging always require a
      // verified factor for the mandatory vendor audience.
      const allowNoMfa =
        typeof window !== "undefined" &&
        /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
      const actorClass = resolveActorClassFromAudience(loadAuthConfig().audience);

      const decision = await resolveVendorMfaGate({
        session: current,
        actorClass,
        allowNoMfa,
        hasVerifiedFactor: vendorHasVerifiedFactor,
      });
      if (cancelled) return;

      if (decision.kind === "enroll-required") {
        // Withhold the session so no protected surface renders behind the gate.
        setSession(null);
        setMfaEnrollmentRequired(true);
        setLoading(false);
        return;
      }

      setSession(decision.session);
      setMfaEnrollmentRequired(false);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      mfaEnrollmentRequired,
      signIn: async (email?: string) => {
        // OIDC PKCE redirect — authoritative session is restored by
        // getCurrentUser() on the callback page load. The on-page form
        // collects ONLY the email, used as login_hint (ENG-3777); the
        // password is entered only on the hosted sign-in page the redirect
        // lands on, which also carries the "Forgot password?" reset link.
        //
        // ENG-101 — previously this discarded the typed email entirely and
        // fired a bare signIn(), which silently accepted whatever identity a
        // stale `.molaris.ai` refresh cookie already carried. prompt:'login'
        // forces the hosted form to render, and login_hint carries the
        // typed email so the server can also fail closed if it doesn't
        // match the cookie session.
        await authSignIn({ prompt: 'login', loginHint: email || undefined });
      },
      startMfaEnrollment: async () => {
        // Route into the hosted auth flow, where the mandatory pool forces
        // factor setup before minting a verified session. prompt:'login'
        // re-renders the hosted form rather than silently resuming the
        // factor-less cookie session. This does NOT touch the sign-in cutover
        // flag path (MFA-M1); it only hands the user to the existing hosted
        // enrollment surface.
        await authSignIn({ prompt: 'login' });
      },
      signOut: async (options?: SignOutOptions) => {
        const result = await authSignOut(options);
        setSession(null);
        setMfaEnrollmentRequired(false);
        return result;
      },
    }),
    [session, loading, mfaEnrollmentRequired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
